// REST + SSE Конструктора (docs/api-flow.md). Свой маленький клиент: общий
// `request` в api.ts не экспортируется, а файл api.ts — интегратора.

import { ApiError, getToken, setToken } from '../../api'
import type {
  FlowArea, EngineResolution, EngineOption, CompilePlan, FlowEdge, FlowNode, NodeType, Scheme, SchemeSummary, SchemeUsage, SuggestionsResponse, Template,
} from './types'

const TIMEOUT = 20_000
const LONG = 180_000

async function req<T>(path: string, init: RequestInit = {}, timeout = TIMEOUT): Promise<T> {
  const headers = new Headers(init.headers)
  const token = getToken()
  if (token) headers.set('X-Agentbox-Token', token)
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const abort = new AbortController()
  const timer = window.setTimeout(() => abort.abort(), timeout)
  let res: Response
  try {
    res = await fetch(`/api${path}`, { ...init, headers, credentials: 'same-origin', signal: abort.signal })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new ApiError(408, 'Сервер не ответил вовремя')
    throw error
  } finally {
    window.clearTimeout(timer)
  }
  if (res.status === 401) {
    setToken(null)
    throw new ApiError(401, 'Сессия оператора истекла')
  }
  if (!res.ok) {
    const raw = await res.text().catch(() => '')
    let detail: unknown = raw
    try { detail = JSON.parse(raw).detail ?? raw } catch { /* текст как есть */ }
    const err = new ApiError(res.status, typeof detail === 'string' ? detail : `Ошибка ${res.status}`)
    ;(err as ApiError & { body?: unknown }).body = detail
    throw err
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) })
const s = (id: string) => encodeURIComponent(id)

export type SayEvent =
  | { type: 'line'; line: FlowNode['log'][number] }
  | { type: 'question'; question: NonNullable<FlowNode['question']> }
  | { type: 'node'; node: FlowNode }
  | { type: 'error'; text: string }
  | { type: 'done' }

export const flowApi = {
  resolveEngines: (scheme: Scheme) => req<Record<string, EngineResolution>>(`/flow/${s(scheme.id)}/resolve-engines`, json("POST", Object.fromEntries(Object.entries(scheme).filter(([key]) => key !== "derived")))),
  engines: () => req<{ items: EngineOption[] }>('/flow/engines'),
  list: () => req<{ schemes: SchemeSummary[] }>('/flow'),
  create: (title: string, doImport = false) => req<Scheme>('/flow', json('POST', { title, import: doImport })),
  get: (id: string) => req<Scheme>(`/flow/${s(id)}`),
  patch: (id: string, body: { title?: string; importDismissed?: boolean; areas?: FlowArea[] }) => req<Scheme>(`/flow/${s(id)}`, json('PATCH', body)),
  remove: (id: string) => req<void>(`/flow/${s(id)}`, { method: 'DELETE' }),
  layout: (id: string, body: { nodes: { id: string; x: number; y: number; w?: number; h?: number }[]; stacks?: { id: string; x: number; y: number }[] }) =>
    req<void>(`/flow/${s(id)}/layout`, json('POST', body)),

  addNode: (id: string, body: { type: NodeType; title?: string; text?: string; x: number; y: number; w?: number }) =>
    req<FlowNode>(`/flow/${s(id)}/nodes`, json('POST', body)),
  patchNode: (id: string, nid: string, body: Partial<Pick<FlowNode, 'title' | 'text' | 'x' | 'y' | 'w' | 'h' | 'engine' | 'skills' | 'source' | 'status'>> & { stack?: null }) =>
    req<FlowNode>(`/flow/${s(id)}/nodes/${s(nid)}`, json('PATCH', body)),
  removeNode: (id: string, nid: string) => req<void>(`/flow/${s(id)}/nodes/${s(nid)}`, { method: 'DELETE' }),
  addEdge: (id: string, body: { from: string; to: string; kind?: string | null }) => req<FlowEdge>(`/flow/${s(id)}/edges`, json('POST', body)),
  removeEdge: (id: string, eid: string) => req<void>(`/flow/${s(id)}/edges/${s(eid)}`, { method: 'DELETE' }),

  templates: (type: NodeType, q = '') => req<{ items: Template[] }>(`/flow/templates?type=${type}&q=${encodeURIComponent(q)}`),
  /** Каталог Библиотек (зона platform-api). Нет эндпоинта — пустой список. */
  library: async (type: NodeType | 'skill', q = ''): Promise<Template[]> => {
    const t = type === 'subagent' ? 'agent' : type
    if (t !== 'skill' && t !== 'agent' && t !== 'mcp') return []
    try {
      const res = await req<{ items: { id: string; name: string; title?: string; description?: string; source?: string; installed?: boolean }[] }>(
        `/library?type=${t}&q=${encodeURIComponent(q)}`,
      )
      return res.items.filter((i) => !i.installed).map((i) => ({
        id: i.id, name: i.name, title: i.title ?? i.name, description: i.description ?? '', source: 'library' as const, path: i.source ?? null,
      }))
    } catch {
      return []
    }
  },
  /** Улучшения схемы (§4.5): пересчёт на каждый запрос. */
  suggestions: (id: string) => req<SuggestionsResponse>(`/flow/${s(id)}/suggestions`, {}, 60_000),
  accept: (id: string, sug: string) =>
    req<{ accepted: { id: string; node: string | null; written: string[]; backupDir: string | null }; scheme: Scheme; suggestions: SuggestionsResponse }>(
      `/flow/${s(id)}/suggestions/${s(sug)}/accept`, json('POST', {}), 60_000),
  reject: (id: string, sug: string) => req<SuggestionsResponse>(`/flow/${s(id)}/suggestions/${s(sug)}/reject`, json('POST', {}), 60_000),
  /** Режим «расход»: строки /api/usage/by-agent, сопоставленные с элементами схемы. */
  schemeUsage: (id: string) => req<SchemeUsage>(`/flow/${s(id)}/usage`),

  compile: (id: string) => req<CompilePlan>(`/flow/${s(id)}/compile`, json('POST', {}), LONG),
  apply: (id: string, overwrite = false) =>
    req<{ ok: boolean; written: string[]; backupDir: string | null }>(`/flow/${s(id)}/apply`, json('POST', { overwrite }), LONG),
  run: (id: string, overwrite = false) =>
    req<{ ok: boolean; written: string[]; backupDir: string | null; sessionId: string }>(`/flow/${s(id)}/run`, json('POST', { overwrite }), LONG),

  voice: (audio: Blob) => {
    const form = new FormData()
    form.append('audio', audio, 'voice.webm')
    return req<{ text: string; durationSec: number }>('/flow/voice', { method: 'POST', body: form }, LONG)
  },
  dictate: (id: string, text: string) => req<{ nodes: FlowNode[]; edges: FlowEdge[] }>(`/flow/${s(id)}/dictate`, json('POST', { text }), LONG),
  proposals: (id: string, accept: boolean) => req<Scheme>(`/flow/${s(id)}/proposals`, json('POST', { accept })),

  /** ↵ в карточке: POST + поток SSE. Каждое событие — в onEvent. */
  async say(id: string, nid: string, text: string, answer: boolean, onEvent: (e: SayEvent) => void): Promise<void> {
    const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' })
    const token = getToken()
    if (token) headers.set('X-Agentbox-Token', token)
    const res = await fetch(`/api/flow/${s(id)}/nodes/${s(nid)}/say`, {
      method: 'POST', headers, credentials: 'same-origin', body: JSON.stringify({ text, answer }),
    })
    if (!res.ok || !res.body) {
      const raw = await res.text().catch(() => '')
      throw new ApiError(res.status, raw || `Ошибка ${res.status}`)
    }
    const reader = res.body.getReader()
    const dec = new TextDecoder()
    let buf = ''
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buf += dec.decode(value, { stream: true })
      let cut: number
      while ((cut = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, cut)
        buf = buf.slice(cut + 2)
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data:')) continue
          try { onEvent(JSON.parse(line.slice(5).trim()) as SayEvent) } catch { /* битая строка */ }
        }
      }
    }
  },
}
